import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import Cookies from "js-cookie";
import ky from "ky";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { useDefaultStore } from "@/stores";

export const Route = createFileRoute("/oauth/$site/callback")({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			code: z.string().optional(),
			error: z.string().optional(),
			error_description: z.string().optional(),
		}),
	),
});

const ERROR_MAP = {
	unsupported_response_type: "지원하지 않는 응답 유형입니다",
	invalid_scope: "요청한 접근 권한이 유효하지 않습니다",
	invalid_client: "유효하지 않은 클라이언트입니다",
	invalid_redirect_uri: "등록되지 않은 콜백 URL입니다",
	access_denied: "접근이 거부되었습니다",
};
const ERROR_DESCRIPTION_MAP = {
	"The response type is not supported": "지원하지 않는 응답 유형입니다",
	"The scope is invalid": "요청한 접근 권한이 유효하지 않습니다",
	"The client id is invalid": "유효하지 않은 클라이언트입니다",
	"The redirect uri is invalid": "등록되지 않은 콜백 URL입니다",
	"The id or password is invalid": "아이디 또는 비밀번호가 올바르지 않습니다",
};

function RouteComponent() {
	const { site } = useParams({ from: Route.id });
	const { code, error, error_description } = useSearch({ from: Route.id });

	const { setToken } = useDefaultStore();

	const [result, setResult] = useState<"processing" | "error">(
		error ? "error" : "processing",
	);

	const [callbackError, setCallbackError] = useState<{
		title: string;
		description?: string;
	} | null>(null);

	const handleGoBackToLogin = useCallback(() => {
		// 콜백 페이지가 히스토리 상에서 이어진 경우(대부분 로그인 팝업 흐름) 먼저 뒤로가기를 시도
		if (window.history.length > 1) {
			window.history.back();
			return;
		}

		// 팝업으로 열린 경우, 부모창(로그인 화면)에 포커스 후 팝업 닫기
		if (window.opener) {
			window.opener.focus();
			window.opener.postMessage(
				{
					type: "oauth-callback",
					success: false,
				},
				"*",
			);
			window.close();
			return;
		}

		// 단독 탭으로 열린 경우 로그인 페이지로 이동
		window.location.assign("/login");
	}, []);

	const handleCallback = useCallback(async () => {
		// 처리 시작 시에는 항상 로딩 화면을 유지(성공 시 실패 UI 플래시 방지)
		setResult("processing");
		setCallbackError(null);

		if (error) {
			const title = ERROR_MAP[error as keyof typeof ERROR_MAP] ?? error;
			const description =
				ERROR_DESCRIPTION_MAP[
					error_description as keyof typeof ERROR_DESCRIPTION_MAP
				] ?? error_description;

			setCallbackError({ title, description });
			setResult("error");
			toast.error(description ?? title);
			return;
		}

		if (!code || !site) {
			setCallbackError({
				title: "로그인에 실패했습니다.",
				description: "요청 정보가 올바르지 않습니다. 다시 시도해주세요.",
			});
			setResult("error");
			return;
		}

		try {
			const response = await ky.post(
				`https://api${import.meta.env.DEV ? "-local" : ""}.library.dxmt.me/oauth/callback`,
				{
					json: {
						code,
						site,
					},
				},
			);

			const data = (await response.json()) as { success: boolean };

			if (data.success) {
				const accessToken = response.headers.get("library-access-token");
				const refreshToken = response.headers.get("library-refresh-token");

				if (accessToken && refreshToken) {
					Cookies.set("library-refresh-token", refreshToken, {
						expires: 14,
					});
					setToken(accessToken);

					window.opener?.postMessage(
						{
							type: "oauth-callback",
							success: true,
						},
						"*",
					);

					window.close();
					return;
				}
			}

			setCallbackError({
				title: "로그인에 실패했습니다.",
				description: "로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.",
			});
			setResult("error");
			toast.error("로그인 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
		} catch {
			setCallbackError({
				title: "로그인에 실패했습니다.",
				description: "네트워크 오류가 발생했습니다. 다시 시도해주세요.",
			});
			setResult("error");
			toast.error("네트워크 오류가 발생했습니다. 다시 시도해주세요.");
		}
	}, [code, error, error_description, site, setToken]);

	useEffect(() => {
		if (code && site && !error) {
			handleCallback();
		}
	}, [code, error, site, handleCallback]);

	return result === "error" ? (
		<div className="flex items-center justify-center min-h-screen">
			<div className="text-center">
				<p className="text-xl text-gray-800 font-bold">
					{callbackError?.title ??
						ERROR_MAP[error as keyof typeof ERROR_MAP] ??
						error}
				</p>
				<p className="text-gray-600 text-base">
					{callbackError?.description ??
						ERROR_DESCRIPTION_MAP[
							error_description as keyof typeof ERROR_DESCRIPTION_MAP
						] ??
						error_description}
				</p>

				<div className="mt-4 flex gap-2 justify-center">
					<Button onClick={handleGoBackToLogin} className="mt-0">
						로그인으로 돌아가기
					</Button>
				</div>
			</div>
		</div>
	) : (
		<div className="flex items-center justify-center min-h-screen">
			<div className="text-center">
				<Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
				<p className="text-gray-600">인증 처리 중...</p>
			</div>
		</div>
	);
}
